import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Columns3, SlidersHorizontal } from "lucide-react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pagination?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  /** Controlled column order (TanStack Table column ids). */
  columnOrder?: string[];
  /** Opens column layout modal (e.g. positions); saves separately from toggles. */
  onCustomizeColumns?: () => void;
  customizeColumnsLabel?: string;
  /** Legacy quick toggles (e.g. transactions table). */
  showColumnPicker?: boolean;
  columnPickerTriggerClassName?: string;
  tableClassName?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  sorting,
  onSortingChange,
  pagination = true,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  columnOrder,
  onCustomizeColumns,
  customizeColumnsLabel = "Columns…",
  showColumnPicker = false,
  columnPickerTriggerClassName,
  tableClassName = "min-w-[980px] md:min-w-full",
}: DataTableProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [uncontrolledVisibility, setUncontrolledVisibility] =
    React.useState<VisibilityState>({});
  const columnVisibility = columnVisibilityProp ?? uncontrolledVisibility;
  const setColumnVisibility =
    onColumnVisibilityChange ?? setUncontrolledVisibility;
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: onSortingChange,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    ...(pagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(columnOrder !== undefined ? { columnOrder } : {}),
    },
  });

  const hideableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanHide());

  return (
    <div className="w-full">
      {(onCustomizeColumns || (showColumnPicker && hideableColumns.length > 0)) && (
        <div className="flex justify-end px-3 pt-3 pb-1 gap-2">
          {onCustomizeColumns && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={onCustomizeColumns}
            >
              <Columns3 className="mr-1.5 h-3.5 w-3.5" />
              {customizeColumnsLabel}
            </Button>
          )}
          {showColumnPicker && hideableColumns.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={columnPickerTriggerClassName ?? "h-8 text-xs"}
                >
                  <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {hideableColumns.map((column) => {
                  const meta = column.columnDef.meta as
                    | { columnLabel?: string }
                    | undefined;
                  const label =
                    meta?.columnLabel ??
                    (typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id);
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {label}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      <Table className={tableClassName}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="border-border/30 hover:bg-transparent"
            >
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | { className?: string; sticky?: boolean }
                  | undefined;
                const metaClassName = meta?.className ?? "";
                const stickyClass = meta?.sticky
                  ? "sticky left-0 z-30 bg-background border-r border-border/30"
                  : "";
                return (
                  <TableHead
                    key={header.id}
                    className={`h-9 px-2 md:px-4 text-sm font-medium text-muted-foreground ${metaClassName} ${stickyClass}`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="border-border/20 hover:bg-muted/30"
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as
                    | { className?: string; sticky?: boolean }
                    | undefined;
                  const metaClassName = meta?.className ?? "";
                  const stickyClass = meta?.sticky
                    ? "sticky left-0 z-20 bg-background border-r border-border/20"
                    : "";
                  return (
                    <TableCell
                      key={cell.id}
                      className={`py-2 px-2 md:px-4 text-sm ${metaClassName} ${stickyClass}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-16 text-center text-sm text-muted-foreground"
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (table.getCanPreviousPage() || table.getCanNextPage()) && (
        <div className="flex items-center justify-end gap-1 px-3 py-2 border-t border-border/30">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
